import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { FileDown, Play, ShieldCheck, ArrowUpRight, RefreshCcw, MapPin, ArrowUp } from "lucide-react";
import api from "@/lib/api";
import ReadinessGauge from "./ReadinessGauge";
import PillarCard from "./PillarCard";
import { useAuth } from "@/context/AuthContext";
import { useDialog } from "@/context/DialogProvider";
import { useSite } from "@/context/SiteContext";

// Which module owns each period pillar — used to drop pillars whose module
// is globally hidden (phased rollout) so the score isn't penalised for a
// feature that's offline.
const PILLAR_MODULE = {
  incidents: "emergency",
  training: "training-coms",
  pest_control: "documentation",
  approved_vendor: "documentation",
};

// Pillars gated by a per-tenant feature flag (from useAuth().effectiveFeatures).
// A pillar is hidden entirely if ALL of its listed features are OFF for the
// tenant. Backend also filters, this is defence-in-depth for the UI.
const PILLAR_FEATURES = {
  daily_kitchen:   ["cooking-record", "hot-cold-hold", "thawing", "metal-detector"],
  pest_control:    ["pest-control"],
  approved_vendor: ["approved-vendor"],
  forms:           ["forms"],
};

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const MONTHLY_PILLARS = ["hygiene", "cleanliness", "vehicle_cleanliness", "ccp", "deviations", "daily_kitchen", "equipment_care", "forms"];
const PERIOD_PILLARS  = ["incidents", "training", "calibration", "fsms_team", "pest_control", "approved_vendor"];

// Pillar key → Audit Vault report_type for the "Generate All Reports" action.
const MONTHLY_VAULT_TYPE = {
  hygiene: "hygiene",
  cleanliness: "cleanliness",
  vehicle_cleanliness: "vehicle_cleanliness",
  ccp: "ccp",
  deviations: "ccp_deviations",
  daily_kitchen: "kitchen_daily",
  equipment_care: "equipment_care",
  forms: "forms",
};

function lastNMonths(n) {
  const out = [];
  const d = new Date();
  d.setDate(1);
  for (let i = 0; i < n; i++) {
    const y = d.getFullYear();
    const m = d.getMonth() + 1;
    const value = `${y}-${String(m).padStart(2, "0")}`;
    const label = i === 0
      ? `${MONTH_NAMES[m - 1]} ${y} (current month)`
      : `${MONTH_NAMES[m - 1]} ${y}`;
    out.push({ value, label });
    d.setMonth(d.getMonth() - 1);
  }
  return out;
}

// Default Start = beginning of current year, End = current month.
function defaultPeriodRange() {
  const d = new Date();
  const y = d.getFullYear();
  const m = d.getMonth() + 1;
  return {
    start: `${y}-01`,
    end:   `${y}-${String(m).padStart(2, "0")}`,
  };
}

// Build a wide month-picker list that covers the entire current year and
// the 12 previous months, so a user can run a period-based report that
// spans a year-end boundary without re-coding the dropdown.
function periodMonthOptions() {
  const out = [];
  const d = new Date();
  d.setDate(1);
  for (let i = 0; i < 24; i++) {
    const y = d.getFullYear();
    const m = d.getMonth() + 1;
    out.push({
      value: `${y}-${String(m).padStart(2, "0")}`,
      label: `${MONTH_NAMES[m - 1]} ${y}`,
    });
    d.setMonth(d.getMonth() - 1);
  }
  return out;
}

const STATUS_BADGE = {
  ready:     { pill: "ok",   label: "Audit-ready" },
  attention: { pill: "warn", label: "Needs attention" },
  critical:  { pill: "err",  label: "Critical gaps" },
};

const MODE_META = {
  monthly: {
    testid: "assessment-panel-monthly",
    title: "Monthly Assessment",
    blurb: (
      <>
        Score the <b>monthly-scope</b> pillars (Hygiene, Cleanliness,
        CCP Data Capture, CCP Deviations, Equipment Care, Custom Forms —
        plus Daily Kitchen Record if your workspace has that add-on) for
        one site and one calendar month. Each ready pillar can be filed
        directly into the Audit Vault.
      </>
    ),
    pillarKeys: MONTHLY_PILLARS,
  },
  period: {
    testid: "assessment-panel-period",
    title: "Period Assessment",
    blurb: (
      <>
        Score the six <b>period-scope</b> pillars (Incidents/NCR,
        Training, Calibration, FSMS Team, Pest Control, Approved
        Vendor) across a custom Start → End month window. Each pillar
        files into the Audit Vault independently so a return-for-rework
        on one doesn&apos;t drag the others through the workflow.
      </>
    ),
    pillarKeys: PERIOD_PILLARS,
  },
};

export default function AssessmentPanel({ sites, mode = "monthly", onAssessmentReady }) {
  const meta = MODE_META[mode] || MODE_META.monthly;
  const { hiddenModules, effectiveFeatures } = useAuth();
  const { notify } = useDialog();
  // Site selection is DRIVEN by the master site filter in the AppShell
  // header. The Assessment panel used to expose its own site dropdown,
  // which collided with the header filter (users would silently pick
  // different sites in each control and then wonder why the pillars
  // showed the wrong data). Single source of truth = header filter.
  const { activeSiteId, isAllSites, activeSite } = useSite();
  const siteId = isAllSites ? "" : activeSiteId;
  const pillarKeys = useMemo(
    () => meta.pillarKeys.filter((p) => {
      if (PILLAR_MODULE[p] && (hiddenModules || []).includes(PILLAR_MODULE[p])) return false;
      const feats = PILLAR_FEATURES[p];
      if (feats && !feats.some((f) => (effectiveFeatures || {})[f] !== false)) return false;
      return true;
    }),
    [meta.pillarKeys, hiddenModules, effectiveFeatures],
  );
  const monthOptions = useMemo(() => lastNMonths(6), []);
  const rangeOptions = useMemo(() => periodMonthOptions(), []);
  const defaultRange = useMemo(() => defaultPeriodRange(), []);

  const [periodValue, setPeriodValue] = useState(monthOptions[0]?.value || "");
  const [startPeriod, setStartPeriod] = useState(defaultRange.start);
  const [endPeriod, setEndPeriod] = useState(defaultRange.end);
  const [busy, setBusy] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [vaultBusy, setVaultBusy] = useState(false);
  const [err, setErr] = useState("");
  const [info, setInfo] = useState("");
  const [data, setData] = useState(null);

  // When the header site filter changes, clear the previously-loaded
  // assessment so the tiles don't misrepresent the newly-selected site.
  useEffect(() => {
    setData(null); setErr(""); setInfo("");
  }, [siteId]);

  // Tell the parent what's currently picked so it can mirror the
  // selection in any sibling "individual report" panels.
  useEffect(() => {
    onAssessmentReady?.({ siteId, periodValue, startPeriod, endPeriod, mode });
  }, [siteId, periodValue, startPeriod, endPeriod, mode, onAssessmentReady]);

  const run = async () => {
    if (!siteId) {
      setErr("Pick a specific site from the header filter above — the assessment is per-site.");
      return;
    }
    if (mode === "period" && startPeriod > endPeriod) {
      setErr("Start month must be on or before End month."); return;
    }
    setBusy(true); setErr(""); setInfo(""); setData(null);
    try {
      const params = new URLSearchParams({ site_id: siteId });
      if (mode === "period") {
        params.append("start_period", startPeriod);
        params.append("end_period", endPeriod);
      } else if (periodValue) {
        params.append("period_value", periodValue);
      }
      params.append("pillars", pillarKeys.join(","));
      const res = await api.get(`/reports/assessment?${params.toString()}`);
      setData(res.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Assessment failed");
    } finally {
      setBusy(false);
    }
  };

  const downloadPdf = async () => {
    // PDF endpoint is still month-scoped (designed around 12-pillar scorecard);
    // only meaningful in monthly mode.
    if (mode !== "monthly" || !siteId || !data) return;
    setPdfBusy(true);
    try {
      const params = new URLSearchParams({ site_id: siteId });
      if (periodValue) params.append("period_value", periodValue);
      const res = await api.get(`/reports/assessment/pdf?${params.toString()}`,
        { responseType: "blob" });
      const cd = res.headers?.["content-disposition"] || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      const site = sites.find((s) => s.id === siteId);
      const fname = m ? m[1] : `${site?.name || "Site"}-Readiness-Snapshot.pdf`;
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = fname; document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Snapshot PDF failed");
    } finally {
      setPdfBusy(false);
    }
  };

  // Monthly mode: file every AVAILABLE monthly report (hygiene, cleanliness,
  // CCP data capture, CCP deviations) into the Audit Vault in one action.
  // Only 100%-complete, not-yet-filed pillars are filed; the rest are
  // reported back so the user knows exactly what was skipped and why.
  const generateForAudit = async () => {
    if (mode !== "monthly" || !siteId || !data) return;
    setVaultBusy(true); setErr(""); setInfo("");
    const targets = (data.pillars || []).filter((p) => MONTHLY_VAULT_TYPE[p.key]);
    let filed = 0, dup = 0, notReady = 0, failed = 0, lastId = "";
    const failMsgs = [];
    for (const p of targets) {
      if (Math.round(Number(p.score) || 0) < 100) { notReady++; continue; }
      if (p.vault_status && p.vault_status.filed) { dup++; continue; }
      try {
        const { data: res } = await api.post("/audit-vault/generate", {
          report_type: MONTHLY_VAULT_TYPE[p.key],
          site_id: siteId,
          period_month: periodValue,
        });
        filed++;
        lastId = res.report?.id || lastId;
      } catch (e) {
        const det = e?.response?.data?.detail;
        if (det && typeof det === "object" && det.code === "duplicate") dup++;
        else if (det && typeof det === "object" && det.code === "incomplete_data") notReady++;
        else if (det && typeof det === "object" && det.code === "signature_required") {
          failed++; failMsgs.push(`${p.label}: ${det.message || "Upload your signature in Profile first."}`);
        } else {
          failed++; failMsgs.push(`${p.label}: ${(det && det.message) || det || "filing failed"}`);
        }
      }
    }
    setVaultBusy(false);

    const parts = [];
    if (filed) parts.push(`${filed} filed`);
    if (dup) parts.push(`${dup} already in vault`);
    if (notReady) parts.push(`${notReady} not yet 100%`);
    if (failed) parts.push(`${failed} failed`);
    const summary = parts.join(" · ") || "Nothing to file";

    if (filed > 0) {
      await notify({
        title: "Reports filed to Audit Vault",
        message: `${summary}. Opening the Vault so you can sign each report as generator.`
          + (failMsgs.length ? `\n\n${failMsgs.join("\n")}` : ""),
        variant: failed ? "warn" : "ok",
      });
      window.location.href = `/app/audit-vault${lastId ? `?focus=${encodeURIComponent(lastId)}` : ""}`;
      return;
    }

    if (failMsgs.length) {
      setErr(failMsgs.join(" · "));
    } else if (dup > 0 && notReady === 0) {
      setInfo(`All available reports are already in the Audit Vault for ${periodValue}.`);
    } else {
      setErr(`No reports filed — ${notReady} report(s) are not yet 100% complete for ${periodValue}. Complete the outstanding items, re-run the assessment, then try again.`);
    }
  };

  const badge = data ? STATUS_BADGE[data.overall_status] : null;
  const periodLabelForChildren = mode === "period"
    ? `${startPeriod}—${endPeriod}`
    : periodValue;
  const periodInverted = mode === "period" && startPeriod > endPeriod;

  return (
    <section className="vb-card p-4 sm:p-6 mb-8" data-testid={meta.testid}>
      <div className="flex items-start gap-3 mb-4">
        <div
          className="flex items-center justify-center w-10 h-10 rounded-xl shrink-0"
          style={{ background: "var(--vb-bg-2)", color: "var(--vb-text-2)" }}
        >
          <ShieldCheck size={20} />
        </div>
        <div className="min-w-0">
          <h2 className="vb-heading text-xl" style={{ color: "var(--vb-text-2)" }}>
            {meta.title}
          </h2>
          <p className="text-sm opacity-70 max-w-3xl">{meta.blurb}</p>
        </div>
      </div>

      {err && (
        <div className="vb-pill err mb-4" data-testid={`${meta.testid}-error`}>{err}</div>
      )}
      {info && (
        <div className="vb-pill ok mb-4" data-testid={`${meta.testid}-info`}>{info}</div>
      )}

      {isAllSites && (
        <div
          className="vb-card p-4 mb-4 flex items-start gap-3"
          style={{ background: "var(--vb-bg-2)", borderColor: "rgba(0,0,0,0.08)" }}
          data-testid={`${meta.testid}-pick-site`}
        >
          <ArrowUp size={18} className="mt-0.5 shrink-0" style={{ color: "var(--vb-text-2)" }} />
          <div className="text-sm">
            <div className="font-semibold" style={{ color: "var(--vb-text-2)" }}>
              Pick a specific site to run this assessment
            </div>
            <div className="opacity-70 mt-0.5">
              The site filter in the top header currently shows{" "}
              <b>All sites</b>. Use it to select a single site — the assessment scores compliance per site, not tenant-wide.
            </div>
          </div>
        </div>
      )}

      {/* Controls */}
      <div className={`grid gap-3 items-end ${
        mode === "period"
          ? "md:grid-cols-[1fr,1fr,1fr,auto]"
          : "md:grid-cols-[1fr,1fr,auto]"
      }`}>
        <div>
          <label className="block text-xs font-semibold mb-1.5 uppercase tracking-wider"
            style={{ color: "var(--vb-text-2)" }}>Site</label>
          <div
            className="vb-input flex items-center gap-2 cursor-not-allowed"
            style={{ opacity: siteId ? 1 : 0.55, background: "var(--vb-bg-2)" }}
            title="Change the active site from the header filter above."
            data-testid={`${meta.testid}-site`}
          >
            <MapPin size={14} className="shrink-0" style={{ color: "var(--vb-text-2)" }} />
            <span className="truncate">
              {siteId
                ? (activeSite?.name || sites.find((s) => s.id === siteId)?.name || "—")
                : "All sites — pick one in the header"}
            </span>
          </div>
        </div>

        {mode === "monthly" && (
          <div>
            <label className="block text-xs font-semibold mb-1.5 uppercase tracking-wider"
              style={{ color: "var(--vb-text-2)" }}>Assessment month</label>
            <select className="vb-input" value={periodValue}
              onChange={(e) => setPeriodValue(e.target.value)}
              data-testid={`${meta.testid}-period`}>
              {monthOptions.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
        )}

        {mode === "period" && (
          <>
            <div>
              <label className="block text-xs font-semibold mb-1.5 uppercase tracking-wider"
                style={{ color: "var(--vb-text-2)" }}>Start month</label>
              <select className="vb-input" value={startPeriod}
                onChange={(e) => setStartPeriod(e.target.value)}
                data-testid={`${meta.testid}-start`}>
                {rangeOptions.map((o) => (
                  <option key={`s-${o.value}`} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1.5 uppercase tracking-wider"
                style={{ color: "var(--vb-text-2)" }}>End month (inclusive)</label>
              <select className="vb-input" value={endPeriod}
                onChange={(e) => setEndPeriod(e.target.value)}
                data-testid={`${meta.testid}-end`}>
                {rangeOptions.map((o) => (
                  <option key={`e-${o.value}`} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
          </>
        )}

        <button
          className="vb-btn"
          onClick={run}
          disabled={!siteId || busy || periodInverted}
          data-testid={`${meta.testid}-run`}
        >
          {busy ? <RefreshCcw size={16} className="animate-spin" /> : <Play size={16} />}
          {busy ? "Running…" : (data ? "Re-run" : "Run assessment")}
        </button>
      </div>

      {periodInverted && (
        <div className="vb-pill warn mt-3" data-testid={`${meta.testid}-range-warning`}>
          Start month must be on or before End month.
        </div>
      )}

      {/* Result */}
      {data && (
        <div className="mt-6 grid gap-5 vb-fade-in" data-testid={`${meta.testid}-result`}>
          {/* Hero scorecard */}
          <div className="flex flex-col md:flex-row items-center md:items-center gap-4 sm:gap-5 p-3 sm:p-5 rounded-2xl overflow-hidden"
            style={{ background: "var(--vb-bg-2)" }}>
            <ReadinessGauge score={data.overall_score} status={data.overall_status} />
            <div className="flex-1 min-w-0 w-full grid gap-2">
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`vb-pill ${badge?.pill || "ok"}`}
                  data-testid={`${meta.testid}-overall-status`}>
                  {badge?.label || ""}
                </span>
                <span className="text-xs opacity-60">· {data.period.label}</span>
                <span className="text-xs opacity-60">· {data.site.name}</span>
              </div>
              <p className="text-sm opacity-80 max-w-2xl">
                Equal-weighted score across {data.pillars.length} pillar
                {data.pillars.length === 1 ? "" : "s"} in this segment.
                Thresholds: <b>100%</b> = ready · <b>70–99%</b> = needs attention ·
                <b> &lt;70%</b> = critical.
              </p>
              <div className="flex flex-col sm:flex-row gap-2 mt-1 sm:flex-wrap">
                <button
                  className="vb-btn ghost w-full sm:w-auto justify-center"
                  onClick={run}
                  disabled={busy}
                  data-testid={`${meta.testid}-refresh`}
                  title="Re-pull every pillar from the database. Use this after editing source data (e.g. a vendor evaluation, a calibration log) so the assessment reflects the very latest state before you file in the Vault."
                >
                  <RefreshCcw size={16} className={busy ? "animate-spin" : ""} />
                  {busy ? "Refreshing…" : "Refresh data"}
                </button>
                {mode === "monthly" && (
                  <>
                    <button
                      className="vb-btn w-full sm:w-auto justify-center"
                      onClick={downloadPdf}
                      disabled={pdfBusy}
                      data-testid={`${meta.testid}-download-snapshot`}
                    >
                      <FileDown size={16} />
                      <span className="sm:inline">
                        {pdfBusy ? "Generating…" : (
                          <>
                            <span className="hidden sm:inline">Download Readiness Snapshot PDF</span>
                            <span className="sm:hidden">Download Snapshot PDF</span>
                          </>
                        )}
                      </span>
                    </button>
                    <button
                      className="vb-btn ghost w-full sm:w-auto justify-center"
                      onClick={generateForAudit}
                      disabled={vaultBusy}
                      data-testid={`${meta.testid}-generate-audit`}
                      title="File every available monthly report (Hygiene, Cleanliness, CCP Data Capture, CCP Deviations, plus Daily Kitchen Record when enabled) that is 100% complete into the Audit Vault, then sign each as generator."
                    >
                      <ShieldCheck size={16} />
                      {vaultBusy ? "Filing…" : "Generate All Reports"}
                    </button>
                  </>
                )}
                {mode === "period" && (
                  // Each period pillar now has its own per-tile filing
                  // button (Incidents/NCR fans out to bulk-file, Training
                  // / Calibration / FSMS file standing snapshots). The
                  // hero stays clean — no redundant buttons.
                  <p className="text-xs opacity-60 self-center">
                    Use each tile&apos;s <b>File / Re-file</b> button below to compile snapshots into the Audit Vault.
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Pillar grid */}
          <div className="grid gap-3 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {data.pillars.map((p) => (
              <PillarCard
                key={p.key}
                pillar={p}
                siteId={siteId}
                periodValue={periodLabelForChildren}
                periodMonthForVault={mode === "monthly" ? periodValue : null}
                bulkPeriodStart={mode === "period" ? startPeriod : null}
                bulkPeriodEnd={mode === "period" ? endPeriod : null}
                onFiled={run}
              />
            ))}
          </div>

          {/* Action items */}
          {data.action_items?.length > 0 && (
            <div className="vb-card p-4" data-testid={`${meta.testid}-actions`}>
              <h3 className="vb-heading text-base mb-2" style={{ color: "var(--vb-text-2)" }}>
                Top action items
              </h3>
              <ul className="grid gap-1.5">
                {data.action_items.map((a, i) => {
                  const am = STATUS_BADGE[a.status] || STATUS_BADGE.attention;
                  return (
                    <li key={i} className="flex items-start gap-3">
                      <span className={`vb-pill ${am.pill} shrink-0 mt-0.5`}>
                        {am.label}
                      </span>
                      <div className="min-w-0 flex-1 text-sm">
                        <div className="font-semibold" style={{ color: "var(--vb-text-2)" }}>
                          {a.pillar_label}
                        </div>
                        <div className="opacity-70">{a.headline}</div>
                      </div>
                      {a.cta_path && (
                        <Link
                          to={a.cta_path}
                          className="inline-flex items-center gap-1 text-xs font-semibold hover:underline shrink-0"
                          style={{ color: "var(--vb-text-2)" }}
                        >
                          Open <ArrowUpRight size={12} />
                        </Link>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
